/**
 * A marca da Previsão de fechamento (11.8, FR-78): o **ponto de previsão** do `DESIGN.md` (9 px,
 * círculo, na cor pastel do item) com o **rótulo por extenso ao lado** — cor nunca é o único sinal
 * (Accessibility Floor). É a mesma marca no card do Funil (nos dois formatos), nos Dados do
 * negócio e no cartão de Configuração → Negócios.
 *
 * A cor vem do token `--pastel-<nome>` de `src/design/tokens.css`, e só dele; o nome da cor viaja
 * pela API, o valor nunca. A paleta pastel **não** é a régua: verde/amarelo/vermelho continuam
 * significando só dias sem contato.
 */

import type { CorPastel, ItensDePrevisao, PrevisaoDeFechamento } from './api'
import './marca-de-previsao.css'

/**
 * O que o leitor de tela ouve antes do rótulo — sem ele, "Mais probabilidade de fechar" solto no
 * card não diz que é a previsão (o ponto é decorativo). Invisível na tela, como os outros sinais.
 */
export const PREFIXO_PARA_LEITOR = 'Previsão: '

/** O ponto de previsão (9 px) — decorativo: quem não distingue a cor lê o rótulo ao lado. */
export function PontoDePrevisao({ cor }: { cor: CorPastel }) {
  return (
    <span
      className="previsao__ponto"
      aria-hidden="true"
      style={{ background: `var(--pastel-${cor})` }}
    />
  )
}

/** O ponto com o rótulo ao lado; `comDescricao` põe a descrição do item embaixo (o modal). */
export function MarcaDePrevisao({
  item,
  classe,
  comDescricao = false,
}: {
  item: Pick<PrevisaoDeFechamento, 'rotulo' | 'cor' | 'descricao'>
  classe?: string
  comDescricao?: boolean
}) {
  return (
    <span className={classe === undefined ? 'previsao__marca' : `previsao__marca ${classe}`}>
      <span className="previsao__marca-linha">
        <PontoDePrevisao cor={item.cor} />
        <span className="previsao__rotulo">
          <span className="previsao__so-para-leitor">{PREFIXO_PARA_LEITOR}</span>
          {item.rotulo}
        </span>
      </span>
      {comDescricao && item.descricao != null && item.descricao !== '' ? (
        <span className="previsao__descricao">{item.descricao}</span>
      ) : null}
    </span>
  )
}

/** O rótulo da previsão que não pôde ser lido — discreto, e nunca confundido com "sem previsão". */
export const PREVISAO_INDISPONIVEL_NO_CARD = 'Previsão marcada (rótulo indisponível)'

/**
 * O que o card (Funil e Hoje) desenha para a previsão de um Negócio: o item rotulado; `'indisponivel'`
 * quando a leitura do rótulo falhou (falha não vira "sem previsão"); `null` sem previsão — ou com o
 * rótulo ainda a caminho: aí nada, nem ponto neutro.
 */
export type PrevisaoNoCard = Pick<PrevisaoDeFechamento, 'rotulo' | 'cor' | 'descricao'> | 'indisponivel' | null

export function previsaoNoCard(
  previsoes: Pick<ItensDePrevisao, 'porId' | 'catalogoFalhou' | 'falharam'>,
  id: string | null | undefined,
): PrevisaoNoCard {
  if (id == null) {
    return null
  }
  const rotulada = previsoes.porId.get(id)
  if (rotulada !== undefined) {
    return rotulada
  }
  return previsoes.catalogoFalhou || previsoes.falharam.has(id) ? 'indisponivel' : null
}

/** A marca da Previsão no card: o ponto com o rótulo, o aviso discreto de falha, ou nada. */
export function MarcaDePrevisaoNoCard({
  previsao,
  classe,
  classeIndisponivel,
}: {
  previsao: PrevisaoNoCard
  classe: string
  classeIndisponivel: string
}) {
  if (previsao === null) {
    return null
  }
  if (previsao === 'indisponivel') {
    return <span className={`${classe} ${classeIndisponivel}`}>{PREVISAO_INDISPONIVEL_NO_CARD}</span>
  }
  return <MarcaDePrevisao item={previsao} classe={classe} />
}
